<script setup>
import { computed } from 'vue'
import { toReativeTime } from '@/utils'

const props = defineProps({
  size: { type: String, default: null },
  loading: { type: Boolean, default: false },
  autoheight: { type: Boolean, default: false },
  img: { type: [Object, String], default: null },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  date: { type: [String, Number], default: '' },
  isVideo: { type: Boolean, default: false },
  playIcon: {
    type: Object,
    default: () => {
      return {}
    }
  },
  link: {
    type: Object,
    default() {
      return { url: '', rel: '', target: '' }
    }
  },
  other: {
    type: Object,
    default() {
      return {}
    }
  }
})
defineEmits(['tracking'])
const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const dateTime = computed(() => toReativeTime(props.date))
</script>

<template>
  <div
    class="tvb-article-card"
    :class="[size ? 'card-' + size : '', { 'card-autoheight': autoheight }]"
  >
    <div v-if="loading" class="tvb-article-card__skeleton tvb-hairline--surround">
      <div class="tvb-article-card__skeleton--image--wrap">
        <tvb-image
          class="tvb-article-card__skeleton--cover"
          src=""
          backgroundc="#F0F1F2"
        ></tvb-image>
      </div>
      <div class="tvb-article-card__skeleton--info">
        <div class="tvb-article-card__skeleton--text"></div>
        <div
          class="tvb-article-card__skeleton--text"
          style="width: 62.5%; margin-top: var(--ac-info-margin-top)"
        ></div>
      </div>
    </div>
    <div v-else class="tvb-article-card__wrap tvb-hairline--surround">
      <tvb-router-link
        :to="link.url"
        :linkRel="link.rel"
        :linkTarget="link.target"
        :class="{ 'disable-hover': isMobile }"
        @click="$emit('tracking')"
      >
        <div class="tvb-article-card__image">
          <div class="tvb-article-card__image--wrap">
            <tvb-image
              class="tvb-article-card__cover"
              :src="img"
              :alt="title"
              :isVideo="isVideo"
              :playIcon="playIcon"
              :transform="isMobile ? false : true"
              backgroundc="#F0F1F2"
            ></tvb-image>
          </div>
        </div>
      </tvb-router-link>
      <tvb-router-link
        :to="link.url"
        :linkRel="link.rel"
        :linkTarget="link.target"
        class="disable-hover"
        @click="$emit('tracking')"
      >
        <div class="tvb-article-card__info">
          <h3 class="clear-h tvb-article-card__info--tit" :title="title">
            <tvb-router-link
              :to="link.url"
              :linkRel="link.rel"
              :linkTarget="link.target"
              :class="{ 'disable-hover': isMobile }"
              @click="$emit('tracking')"
            >
              {{ title }}
            </tvb-router-link>
          </h3>
          <div v-if="subtitle || date" class="tvb-article-card__info--bottom">
            <span class="tvb-article-card__info--author" :title="subtitle">{{ subtitle }}</span>
            <span v-if="date" class="tvb-article-card__info--date">{{ dateTime }}</span>
          </div>
        </div>
      </tvb-router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-card {
  --ac-bg-color: #f8f9fa;
  // --ac-img-bg-color: #f0f1f2;
  --ac-title-font-size: 18px;
  --ac-title-line-height: 24px;
  --ac-title-font-color: #333;
  --ac-subtitle-font-size: 14px;
  --ac-subtitle-line-height: 20px;
  --ac-subtitle-font-color: #818d95;
  --ac-info-margin-top: 10px;
  --ac-info-padding: 0 16px 12px;
  --ac-subtitle-margin-top: 4px;
  --ac-cover-radio: 56.25%; // 如何计算值？ 9 / 16（高除以宽）
  --ac-hover-color: #0062a3;
  --ac-border-radius: 8px;
  --ac-img-border-radius: 8px;
  position: relative;

  a {
    text-decoration: none;
    background-color: transparent;
    color: inherit;
    transition: color 0.15s linear;
    cursor: pointer;
    &:visited {
      color: inherit;
    }
  }
  a:not(.disable-hover):hover {
    color: var(--ac-hover-color) !important;
  }

  .tvb-article-card__wrap {
    background-color: var(--ac-bg-color);
    border-radius: var(--ac-border-radius);
    --tvb-hairline-border-radius: calc(2 * var(--ac-border-radius));
  }
  // img样式
  .tvb-article-card__image {
    position: relative;
    // z-index: 1;
    border-radius: var(--ac-img-border-radius);
  }
  .tvb-article-card__image--wrap {
    padding-top: var(--ac-cover-radio);
    border-radius: inherit;
    background-color: var(--ac-img-bg-color);
    cursor: pointer;
  }
  .tvb-article-card__cover {
    position: absolute;
    top: 0;
    left: 0;
    // z-index: 1;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    overflow: hidden;
    @include safari-overflow-hidden();
  }

  // 内容样式
  .tvb-article-card__info {
    margin-top: var(--ac-info-margin-top);
    padding: var(--ac-info-padding);
    min-height: calc(
      var(--ac-title-line-height) * 2 + var(--ac-subtitle-line-height) +
        var(--ac-subtitle-margin-top)
    );

    .tvb-article-card__info--tit {
      color: var(--ac-title-font-color);
      font-size: var(--ac-title-font-size);
      line-height: var(--ac-title-line-height);
      height: calc(2 * var(--ac-title-line-height));
      letter-spacing: 0.5px;
      word-break: break-all;
      @include line-text-ellipsis(2);

      & > a {
        color: var(--ac-title-font-color);
      }
    }

    .tvb-article-card__info--bottom {
      margin-top: var(--ac-subtitle-margin-top);
      color: var(--ac-subtitle-font-color);
      font-size: var(--ac-subtitle-font-size);
      display: flex;
      align-items: center;
      display: inline-flex;
    }

    .tvb-article-card__info--author {
      flex: 1;
      word-break: break-word;
      line-height: var(--ac-subtitle-line-height);
      @include line-text-ellipsis(1);
    }

    .tvb-article-card__info--date {
      margin-left: 12px;
      line-height: var(--ac-subtitle-line-height);
    }
  }

  // 动态高度样式
  &.card-autoheight {
    .tvb-article-card__info {
      min-height: auto;
      .tvb-article-card__info--tit {
        height: auto;
      }
    }
  }

  &.card-s,
  &.card-xs {
    --ac-title-font-size: 16px;
    --ac-title-line-height: 22px;
    --ac-subtitle-font-size: 13px;
    --ac-subtitle-line-height: 18px;
    --ac-info-margin-top: 8px;
    --ac-info-padding: 0 10px 10px;
    --ac-subtitle-margin-top: 4px;
    --ac-cover-radio: 56.28415300546448%; // 如何计算值？ 9 / 16（高除以宽）
    --ac-border-radius: 5px;
    --ac-img-border-radius: 6px;
  }

  // 骨架屏样式
  .tvb-article-card__skeleton {
    background-color: var(--ac-bg-color);
    border-radius: var(--ac-border-radius);
    --tvb-hairline-border-radius: calc(2 * var(--ac-border-radius));
    .tvb-article-card__skeleton--image--wrap {
      position: relative;
      padding-top: var(--ac-cover-radio);
      border-radius: var(--ac-img-border-radius);
      .tvb-article-card__skeleton--cover {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        overflow: hidden;
        @include safari-overflow-hidden();
      }
    }
    .tvb-article-card__skeleton--info {
      margin-top: var(--ac-info-margin-top);
      padding: var(--ac-info-padding);
      .tvb-article-card__skeleton--text {
        height: 26px;
        background-color: #f0f1f2;
        border-radius: 4px;
      }
    }
  }
}
</style>
